import { useCallback, useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
} from "react-router-dom";
import Constants from "./params/constants";
import Popups from "./components/complex/popup";
import AppShell from "./pages";
import LoginScreen from "./pages/LoginScreen";
import { adminCheckAuth, adminLogout } from "./helpers/auth";
import "./scss/style.scss";

const AUTH_CHECK_ATTEMPTS = 15;
const AUTH_CHECK_INTERVAL_MS = 1500;
const AUTH_CHECK_TIMEOUT_MS = 4000;

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error("timeout")), ms),
    ),
  ]);
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Probe the API until it reports a definitive auth state. "unknown" (network
 * error / server still booting) is retried a few times so a cold start or the
 * test stack's late-bound API doesn't trap the user on the login screen.
 */
async function resolveAuthState(): Promise<"authed" | "unauthed"> {
  for (let i = 0; i < AUTH_CHECK_ATTEMPTS; i++) {
    let result: "authed" | "unauthed" | "unknown" | "error";
    try {
      result = await withTimeout(adminCheckAuth(), AUTH_CHECK_TIMEOUT_MS);
    } catch {
      result = "error";
    }
    if (result === "authed" || result === "unauthed") return result;
    await delay(AUTH_CHECK_INTERVAL_MS);
  }
  // Never block forever: fall back to the login screen.
  return "unauthed";
}

/** checking → probing the API; authed → signed in; unauthed → show login. */
type AuthPhase = "checking" | "authed" | "unauthed";

const App = () => {
  const [phase, setPhase] = useState<AuthPhase>("checking");

  const enter = useCallback(() => setPhase("authed"), []);

  const logout = useCallback(async () => {
    // Best-effort server-side revoke + clear local token, then drop back to
    // the login screen and tear down the live subscription client.
    await adminLogout();
    Constants.dispose();
    setPhase("unauthed");
  }, []);

  useEffect(() => {
    let cancelled = false;
    resolveAuthState().then((state) => {
      if (cancelled) return;
      setPhase(state);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const authed = phase === "authed";

  useEffect(() => {
    if (authed) Constants.init();
  }, [authed]);

  return (
    <Router>
      <div className="App ib-app">
        {phase === "checking" ? (
          <div className="ib-login-screen">
            <div className="ib-login-loading">Loading…</div>
          </div>
        ) : phase === "unauthed" ? (
          <LoginScreen onSuccess={enter} />
        ) : (
          <>
            <Routes>
              <Route path="/" element={<Navigate to="/accounts" replace />} />
              <Route path="/*" element={<AppShell onLogout={logout} />} />
            </Routes>
            <Popups />
          </>
        )}
      </div>
    </Router>
  );
};

export default App;
